import { useState, useCallback, useMemo } from 'react'
import {
  useWarrantyCategories,
  useCreateWarrantyCategory,
  useUpdateWarrantyCategory,
  useDeleteWarrantyCategory,
} from '@/admin/hooks/use-warranties'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { WarrantyCategory } from '@/admin/types/warranties'
import { cn } from '@/shared/lib/utils'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'

import { toastApiError } from '@/shared/lib/api-error'
export function WarrantyCategoriesPage() {
  const { t } = useTranslation()
  const { data: categories, isLoading } = useWarrantyCategories()
  const updateCategory = useUpdateWarrantyCategory()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState<WarrantyCategory | null>(null)

  const sorted = [...(categories ?? [])].sort((a, b) => a.display_order - b.display_order)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = sorted.findIndex((c) => c.uuid === active.id)
      const newIndex = sorted.findIndex((c) => c.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const reordered = [...sorted]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)

      // Update display_order for every category whose position changed.
      // PUT is a full update so each payload includes all required fields.
      const updates = reordered
        .map((cat, idx) => ({ cat, newOrder: idx }))
        .filter(({ cat, newOrder }) => cat.display_order !== newOrder)

      try {
        await Promise.all(
          updates.map(({ cat, newOrder }) =>
            updateCategory.mutateAsync({
              uuid: cat.uuid,
              data: {
                name: cat.name,
                slug: cat.slug,
                description: cat.description,
                display_order: newOrder,
              },
            }),
          ),
        )
      } catch (err) {
        toastApiError(err, t('warrantyCategories.toasts.reorderFailed', 'Failed to reorder categories'));
      }
    },
    [sorted, updateCategory, t],
  )

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <span className="clara-section-label">
          {t('warrantyCategories.title', 'Warranty Categories')}
        </span>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="flex h-auto cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('warrantyCategories.addCategory', 'Add Category')}
        </Button>
      </div>
      {isLoading ? (
        <p className="text-[13px] text-[var(--ink-3)]">{t('common.loading')}</p>
      ) : !sorted.length ? (
        <p className="text-[13px] text-[var(--ink-3)]">
          {t('warrantyCategories.emptyState', 'No warranty categories yet.')}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sorted.map((c) => c.uuid)} strategy={verticalListSortingStrategy}>
            <div className="overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)]">
              {sorted.map((cat, idx) => (
                <SortableCategoryRow
                  key={cat.uuid}
                  cat={cat}
                  isLast={idx === sorted.length - 1}
                  onEdit={() => setEditingCategory(cat)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {/* Add dialog */}
      <CategoryDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
      {/* Edit dialog */}
      {editingCategory && (
        <CategoryDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditingCategory(null)
          }}
          existingCategory={editingCategory}
        />
      )}
    </div>
  );
}

function SortableCategoryRow({
  cat,
  isLast,
  onEdit,
}: {
  cat: WarrantyCategory
  isLast: boolean
  onEdit: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: cat.uuid,
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      className={cn(
        'group flex items-center gap-3 bg-[var(--bg)] px-4 py-3 transition-colors duration-100 hover:bg-[var(--bg-subtle)]',
        !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        className="h-5 w-5 shrink-0 cursor-grab text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)] active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </Button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-[var(--ink)]">{cat.name}</span>
          <Badge variant="outline" className="text-[10px]">
            {cat.slug}
          </Badge>
        </div>
        {cat.description && (
          <span className="text-[11px] text-[var(--ink-3)]">{cat.description}</span>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          title="Edit"
          className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <DeleteCategoryButton category={cat} />
      </div>
    </div>
  );
}

function DeleteCategoryButton({ category }: { category: WarrantyCategory }) {
  const { t } = useTranslation()
  const deleteCategory = useDeleteWarrantyCategory()
  const [deleteOpen, setDeleteOpen] = useState(false)

  async function handleDelete() {
    try {
      await deleteCategory.mutateAsync(category.uuid)
      toast.success(t('warrantyCategories.toasts.deleted', 'Category deleted'))
      setDeleteOpen(false)
    } catch (err) {
      toastApiError(err, t('warrantyCategories.toasts.deleteFailed', 'Failed to delete category'));
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setDeleteOpen(true)}
        title="Delete"
        className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
      >
        <Trash2 className="h-3 w-3" />
      </Button>
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${category.name}?`}
        description={
          <>
            Delete <strong>{category.name}</strong>? This action cannot be undone.
          </>
        }
        onConfirm={handleDelete}
        isPending={deleteCategory.isPending}
      />
    </>
  )
}

function CategoryDialog({
  open,
  onOpenChange,
  existingCategory,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingCategory?: WarrantyCategory
}) {
  const { t } = useTranslation()
  const createCategory = useCreateWarrantyCategory()
  const updateCategory = useUpdateWarrantyCategory()
  const { data: categories } = useWarrantyCategories()

  const isEdit = !!existingCategory
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: t('common.name', 'Name'),
        placeholder: t('warrantyCategories.namePlaceholder', 'e.g. Extended Warranty'),
        value: existingCategory?.name ?? '',
        autoFocus: true,
        required: 'Name is required',
        onChange: (value, formContext) => {
          if (!isEdit) {
            formContext?.setFieldValue(
              'slug',
              String(value ?? '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, ''),
            )
          }
        },
      },
      {
        type: 'text',
        name: 'slug',
        label: t('warrantyCategories.slug', 'Slug'),
        placeholder: 'extended-warranty',
        value: existingCategory?.slug ?? '',
        required: 'Slug is required',
      },
      {
        type: 'text',
        name: 'description',
        label: t('common.description', 'Description'),
        placeholder: t('warrantyCategories.descriptionPlaceholder', 'Optional description'),
        value: existingCategory?.description ?? '',
      },
    ],
    [existingCategory?.description, existingCategory?.name, existingCategory?.slug, isEdit, t],
  )
  const { Form, ref } = useForm(
    {
      name: `warranty-category-dialog-${existingCategory?.uuid ?? 'new'}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      try {
        if (isEdit && existingCategory) {
          await updateCategory.mutateAsync({
            uuid: existingCategory.uuid,
            data: {
              name: values.name.trim(),
              slug: values.slug.trim(),
              description: values.description.trim(),
              display_order: existingCategory.display_order,
            },
          })
          toast.success(t('warrantyCategories.toasts.updated', 'Category updated'))
        } else {
          const maxOrder = (categories ?? []).reduce(
            (max, c) => Math.max(max, c.display_order),
            -1,
          )
          await createCategory.mutateAsync({
            name: values.name.trim(),
            slug: values.slug.trim(),
            description: values.description.trim(),
            display_order: maxOrder + 1,
          })
          toast.success(t('warrantyCategories.toasts.created', 'Category created'))
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(
          err,
          isEdit
            ? t('warrantyCategories.toasts.updateFailed', 'Failed to update category')
            : t('warrantyCategories.toasts.createFailed', 'Failed to create category'),
        )
      }
    },
    [existingCategory?.uuid ?? 'new', open, categories?.length ?? 0],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          ref.current?.resetForm()
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('warrantyCategories.editCategory', 'Edit Category')
              : t('warrantyCategories.addCategory', 'Add Category')}
          </DialogTitle>
        </DialogHeader>
        <Form className="flex flex-col gap-4 py-2" />
        <DialogFooter>
          <Button
            type="submit"
            form={`warranty-category-dialog-${existingCategory?.uuid ?? 'new'}`}
            disabled={createCategory.isPending || updateCategory.isPending}
            className="rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-40"
          >
            {isEdit ? t('common.save', 'Save') : t('common.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
